import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  NgZone,
  provideAppInitializer,
  provideEnvironmentInitializer,
} from '@angular/core';
import * as Sentry from '@sentry/angular';

import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeSr from '@angular/common/locales/sr';
import { enableProdMode } from '@angular/core';
import { getApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestore, AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireFunctionsModule } from '@angular/fire/compat/functions';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  Router,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { akitaDevtools, DevtoolsOptions } from '@datorama/akita';
import { initializeApp } from 'firebase/app';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { AuthService } from './auth/services/auth.service';
import { AuthInterceptor } from './auth/interceptors/auth.interceptor';
import { isInSessionTeardownWindow } from './auth/utils/session-ended-notice';
import { AuthGuard } from './auth/guards/auth.guard';
import { DatePipe } from '@angular/common';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { USE_EMULATOR as USE_AUTH_EMULATOR } from '@angular/fire/compat/auth';
import { USE_EMULATOR as USE_FIRESTORE_EMULATOR } from '@angular/fire/compat/firestore';
import { REGION, USE_EMULATOR as USE_FUNCTIONS_EMULATOR } from '@angular/fire/compat/functions';
import { RolePreloadStrategy } from './core/strategies/role-preload.strategy';
import { BrandService } from './core/services/brand.service';
import { PwaService } from './core/services/pwa.service';
import { ReloadOnChunkErrorHandler } from './core/services/reload-on-chunk-error.handler';
import { VersionService } from './core/services/version.service';
import { OrderStatusPipe } from './shared/pipes/order-status.pipe';
import { UserRolePipe } from './shared/pipes/user-role.pipe';
import { CountryCurrencyConverterPipe } from './shared/pipes/country-currency-converter.pipe';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, noop } from 'rxjs';
import { provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { TranslocoHttpLoader } from './transloco-loader';
import { TranslocoMatPaginatorIntl } from './transloco-mat-paginator-intl';
import { TranslocoTitleStrategy } from './transloco-title-strategy';
import { TitleStrategy } from '@angular/router';
import { APP_LANGS, resolveAppLanguage } from './transloco-runtime';
import { version } from '../../package.json';
import { BUILD_HASH, BUILD_TIMESTAMP } from '../build-info';

const sentryRelease = `tefter-web@${version}+${BUILD_HASH}`;
console.log(`version ${version}+${BUILD_HASH} (built ${BUILD_TIMESTAMP})`);

// Register Serbian locale for proper number/currency formatting
registerLocaleData(localeSr);

if (environment.production) {
  enableProdMode();

  // Console errors remain available as breadcrumbs; handled failures need explicit Sentry capture.
  (['log', 'debug', 'info', 'warn'] as const).forEach((method) => {
    console[method] = noop;
  });
}

// Local dev modes — env 'local' and any '<env>-local' mirror (e.g. rc-prod-local) — must not
// emit telemetry: they run against real backends/data and would ship session replays + PII to Sentry.
const isLocalDev = environment.env === 'local' || environment.env.endsWith('-local');

/**
 * Removes the URL fragment from anything Sentry is about to send.
 *
 * On the public magic-link page the fragment IS the bearer capability that
 * confirms or rejects a real order (ADR 0035). `main.ts` strips it before this
 * module is even evaluated, which covers the initial load; this closes the
 * remaining path, where a second magic link pasted into the same tab produces a
 * hashchange long after Sentry is running. Applied unconditionally rather than
 * only on `/p/order` — no URL in this app carries a fragment worth reporting.
 */
function stripUrlFragment(url: string | undefined): string | undefined {
  if (!url) return url;
  const hashIndex = url.indexOf('#');
  return hashIndex === -1 ? url : url.slice(0, hashIndex);
}

/** True when the event is Firestore's listener permission-denied error. */
function isFirestorePermissionDenied(event: Sentry.ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(
    (value) => value.type === 'FirebaseError' && (value.value ?? '').includes('Missing or insufficient permissions')
  );
}

// Session Replay is the one Sentry channel no `beforeSend*` hook can clean, so
// the public magic-link page does not get one.
//
// Replay ships its own recording events and its own `urls` context, neither of
// which passes through `beforeSend`, `beforeSendTransaction` or
// `beforeBreadcrumb`. Two of its channels carry the fragment: it builds
// performance spans from the SAME Navigation Timing entry that forced the
// `beforeSendTransaction` scrub below (the entry keeps the URL the browser was
// originally navigated to, so the `main.ts` pre-init strip cannot reach it), and
// its history handler pushes the post-hashchange URL into the replay event's
// `urls`. Suppressing the integration on this route closes both at the source.
//
// The route is only ever entered by a real navigation — a supplier opening the
// emailed link, or an operator pasting a revealed one — so reading the pathname
// at init is sufficient; the app itself never links into `p/order`.
//
// It also settles a second question: the recorded party here is an unregistered
// guest with no account, and `maskAllText: false` would otherwise ship their
// order lines and their free-text rejection note to Sentry.
const isPublicInvitationPage = window.location.pathname.startsWith('/p/order');

if (!isLocalDev) {
  Sentry.init({
    dsn: 'https://b89df8a7b487b4c4db9f051f2b2abde1@o4509309090136064.ingest.de.sentry.io/4509311540658257',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    environment: environment.env,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(isPublicInvitationPage
        ? []
        : [
            Sentry.replayIntegration({
              maskAllText: false,
              maskAllInputs: false,
              // Defence in depth for every OTHER route: recording events bypass
              // all three hooks below and reach only this one. Navigation and
              // resource spans are described by a raw URL, so any fragment that
              // ever rides one is scrubbed here too.
              beforeAddRecordingEvent: (event) => {
                const payload = (event.data as { tag?: string; payload?: { description?: string } })?.payload;
                if (payload?.description) {
                  payload.description = stripUrlFragment(payload.description);
                }
                return event;
              },
            }),
          ]),
    ],
    beforeBreadcrumb: (breadcrumb) => {
      if (breadcrumb.category === 'navigation' && breadcrumb.data) {
        breadcrumb.data['from'] = stripUrlFragment(breadcrumb.data['from']);
        breadcrumb.data['to'] = stripUrlFragment(breadcrumb.data['to']);
      }
      return breadcrumb;
    },
    beforeSend: (event) => {
      // An involuntary session teardown signs Firebase out while feature
      // Firestore listeners are still attached; each one then emits a
      // permission-denied FirebaseError before the login navigation destroys
      // it. Those are expected consequences of the sign-out, not defects —
      // drop them inside the teardown window.
      if (isInSessionTeardownWindow() && isFirestorePermissionDenied(event)) {
        return null;
      }
      if (event.request?.url) {
        event.request.url = stripUrlFragment(event.request.url);
      }
      return event;
    },
    // Transactions need their own hook — `beforeSend` never sees them — and the
    // leak here is not the live URL. `browserTracingIntegration` builds its
    // browser-metrics spans (domContentLoadedEvent, etc.) from the Navigation
    // Timing entry, which records the URL the browser was ORIGINALLY navigated
    // to, fragment included. `history.replaceState` does not rewrite performance
    // entries, so the pre-init strip in main.ts cannot reach these — verified on
    // stage, where a span description carried the whole magic-link token.
    beforeSendTransaction: (event) => {
      event.transaction = stripUrlFragment(event.transaction);
      if (event.request?.url) {
        event.request.url = stripUrlFragment(event.request.url);
      }
      for (const span of event.spans ?? []) {
        span.description = stripUrlFragment(span.description);
      }
      return event;
    },
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost'],
    // Session Replay
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    release: sentryRelease,
  });
}

function fetchVersion(buildDetailsService: VersionService) {
  return () => buildDetailsService.getVersion().then((version) => version);
}

function provideErrorHandler() {
  const baseHandler = !isLocalDev ? Sentry.createErrorHandler() : new ErrorHandler();
  return new ReloadOnChunkErrorHandler(baseHandler);
}

function provideAkitaDevtools(options: Partial<DevtoolsOptions> = {}) {
  return provideEnvironmentInitializer(() => {
    akitaDevtools(inject(NgZone), options);
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withPreloading(RolePreloadStrategy),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled',
      }),
      withRouterConfig({
        paramsInheritanceStrategy: 'emptyOnly',
      }),
      withNavigationErrorHandler((error) => {
        inject(ErrorHandler).handleError(error.error ?? error);
      })
    ),
    importProvidersFrom(
      BrowserModule,
      MatSnackBarModule,
      MatIconModule,
      MatFormFieldModule,
      AngularFireModule.initializeApp(environment.firebase),
      AngularFirestoreModule,
      AngularFireAuthModule,
      AngularFireFunctionsModule,
      //NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' }),
      // SW scripts live at the site root so ngsw resolves 'ngsw.json' → /ngsw.json.
      // Named app-sw*.js (not main-sw*.js) to avoid matching the '/main-*.js' glob in
      // ngsw-config.json, which would incorrectly pull the SW wrapper into the prefetch list.
      ServiceWorkerModule.register(environment.production ? 'app-sw.js' : 'app-sw-dev.js', {
        enabled: environment.env !== 'local',
        registrationStrategy: 'registerImmediately',
      })
    ),
    // CoreModule providers
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    DatePipe,
    // i18n — Transloco
    provideTransloco({
      config: {
        availableLangs: [...APP_LANGS],
        defaultLang: 'sr',
        fallbackLang: 'sr',
        reRenderOnLangChange: true,
        prodMode: environment.production,
        missingHandler: { useFallbackTranslation: true, logMissingKey: false },
      },
      loader: TranslocoHttpLoader,
    }),
    { provide: TitleStrategy, useClass: TranslocoTitleStrategy },
    provideTranslocoPersistLang({
      getLangFn: ({ cachedLang, defaultLang }) => resolveAppLanguage(cachedLang, defaultLang),
      storage: { useValue: localStorage },
    }),
    provideTranslocoMessageformat(),
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      // Preload root translations for the active language before the app renders.
      // TranslocoPersistLangService stores the selected language under key 'translocoLang'
      // in localStorage; read it directly (APP_INITIALIZER factories run in parallel, so
      // we can't rely on ordering relative to the persist-lang initializer).
      const lang = resolveAppLanguage(localStorage.getItem('translocoLang'), transloco.getDefaultLang());
      transloco.setActiveLang(lang);
      return firstValueFrom(transloco.load(lang));
    }),
    { provide: MatPaginatorIntl, useClass: TranslocoMatPaginatorIntl },
    // AuthModule providers
    AuthGuard,
    { provide: FIREBASE_OPTIONS, useValue: environment.firebase },
    // Auth emulator is connected by the modular provideAuth() below.
    // Do NOT also set USE_AUTH_EMULATOR — the compat module calls auth.useEmulator()
    // without try/catch, which throws after the modular API already configured it.
    { provide: USE_AUTH_EMULATOR, useValue: undefined },
    { provide: USE_FIRESTORE_EMULATOR, useValue: environment.useEmulators ? ['localhost', 8082] : undefined },
    { provide: USE_FUNCTIONS_EMULATOR, useValue: environment.useEmulators ? ['localhost', 5001] : undefined },
    { provide: REGION, useValue: 'europe-west1' },
    {
      provide: ErrorHandler,
      useFactory: provideErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    // Brand resolution — must run FIRST, blocks bootstrap until resolved
    provideAppInitializer(() => {
      const brandService = inject(BrandService);
      return brandService.initializeBrand();
    }),
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    AngularFirestore,
    // SharedModule providers
    OrderStatusPipe,
    UserRolePipe,
    CountryCurrencyConverterPipe,
    ...(!environment.production ? [provideAkitaDevtools()] : []),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    providePerformance(() => getPerformance(getApp())),
    provideStorage(() => getStorage(getApp())),
    provideFirestore(() => {
      const firestore = getFirestore(getApp());
      if (environment.useEmulators) {
        try {
          connectFirestoreEmulator(firestore, 'localhost', 8082);
        } catch {
          /* already connected */
        }
      }
      return firestore;
    }),
    provideAuth(() => {
      const auth = getAuth(getApp());
      if (environment.useEmulators) {
        try {
          connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        } catch {
          /* already connected by compat module */
        }
      }
      return auth;
    }),
    provideMessaging(() => getMessaging(getApp())),
    provideFunctions(() => getFunctions(getApp(), 'europe-west1')),
    provideAppInitializer(() => {
      const pwaService = inject<PwaService>(PwaService);
      const authService = inject<AuthService>(AuthService);
      return pwaService.initPwaPrompt(authService); // Ensure this returns void, Promise, or Observable
    }),
    provideAppInitializer(() => {
      fetchVersion(inject<VersionService>(VersionService));
    }),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideNativeDateAdapter(),
  ],
};
